import Logger from "@mm/logging";

const logger = Logger.fromFilename(__filename);

/**
 * `html-to-image` embeds every `@font-face` of the document into each capture as base64.
 * `material-symbols` alone ships ~12 MB of woff2, so every single capture had to fetch, encode,
 * serialize and decode ~16 MB of font data, even when no icon was part of the captured tree.
 * We embed text fonts always, but icon fonts only if the captured tree really renders with them.
 */
const ICON_FONT_FAMILY_PATTERN = /^material (symbols|icons)/i;
const FONT_FACE_RULE_TYPE = 5;
const CSS_URL_PATTERN = /url\((["']?)([^)"']+)\1\)/g;

export type FontFaceDescriptor = {
  family: string;
  cssText: string;
  baseUrl: string;
};

const embeddedFontFaceCache = new Map<string, Promise<string>>();

function normalizeFamily(family: string): string {
  return family.trim().replace(/^["']|["']$/g, "");
}

export function parseFontFamilies(fontFamilyValue: string): string[] {
  return fontFamilyValue.split(",").map(normalizeFamily).filter(Boolean);
}

export function isIconFontFamily(family: string): boolean {
  return ICON_FONT_FAMILY_PATTERN.test(normalizeFamily(family));
}

/**
 * Text fonts are always kept, icon fonts only when one of the captured elements uses them.
 */
export function selectFontFacesForExport(
  fontFaces: FontFaceDescriptor[],
  usedFamilies: ReadonlySet<string>,
): FontFaceDescriptor[] {
  return fontFaces.filter(({ family }) => !isIconFontFamily(family) || usedFamilies.has(normalizeFamily(family)));
}

function addFontFamiliesOf(element: Element, usedFamilies: Set<string>) {
  for (const family of parseFontFamilies(window.getComputedStyle(element).fontFamily)) {
    usedFamilies.add(family);
  }
}

/**
 * Collects the font families rendered by the HTML part of the captured tree (incl. shadow roots).
 * SVG subtrees (Highcharts) are skipped on purpose: they can contain thousands of nodes and only
 * ever use the inherited text font.
 */
export function collectUsedFontFamilies(root: Element): Set<string> {
  const usedFamilies = new Set<string>();
  const visit = (element: Element) => {
    addFontFamiliesOf(element, usedFamilies);
    if (element.shadowRoot) {
      for (const shadowChild of Array.from(element.shadowRoot.querySelectorAll(":not(svg *)"))) {
        visit(shadowChild);
      }
    }
  };

  visit(root);
  for (const child of Array.from(root.querySelectorAll(":not(svg *)"))) {
    visit(child);
  }
  return usedFamilies;
}

function collectFontFaces(rules: CSSRuleList, sheetBaseUrl: string, into: FontFaceDescriptor[]) {
  for (const rule of Array.from(rules)) {
    if (rule.type === FONT_FACE_RULE_TYPE) {
      const fontFaceRule = rule as CSSFontFaceRule;
      into.push({
        family: normalizeFamily(fontFaceRule.style.getPropertyValue("font-family")),
        cssText: fontFaceRule.cssText,
        baseUrl: sheetBaseUrl,
      });
    } else if ("styleSheet" in rule && (rule as CSSImportRule).styleSheet) {
      collectFontFacesFromSheet((rule as CSSImportRule).styleSheet as CSSStyleSheet, into);
    } else if ("cssRules" in rule && (rule as CSSGroupingRule).cssRules) {
      collectFontFaces((rule as CSSGroupingRule).cssRules, sheetBaseUrl, into);
    }
  }
}

function collectFontFacesFromSheet(sheet: CSSStyleSheet, into: FontFaceDescriptor[]) {
  try {
    collectFontFaces(sheet.cssRules, sheet.href ?? document.baseURI, into);
  } catch {
    // Cross-origin stylesheet: its rules can not be read, same as html-to-image's own limitation.
  }
}

function getDocumentFontFaces(): FontFaceDescriptor[] {
  const fontFaces: FontFaceDescriptor[] = [];
  for (const sheet of Array.from(document.styleSheets)) {
    collectFontFacesFromSheet(sheet, fontFaces);
  }
  return fontFaces;
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

async function embedFontFace({ cssText, baseUrl }: FontFaceDescriptor): Promise<string> {
  const urls = new Set<string>();
  for (const match of cssText.matchAll(CSS_URL_PATTERN)) {
    if (!match[2].startsWith("data:")) {
      urls.add(match[2]);
    }
  }

  let embeddedCssText = cssText;
  await Promise.all(
    Array.from(urls).map(async (url) => {
      const response = await fetch(new URL(url, baseUrl).href);
      if (!response.ok) {
        throw new Error(`Font request failed with status ${response.status}`);
      }
      const dataUrl = await blobToDataUrl(await response.blob());
      embeddedCssText = embeddedCssText.split(url).join(dataUrl);
    }),
  );
  return embeddedCssText;
}

function getEmbeddedFontFace(fontFace: FontFaceDescriptor): Promise<string> {
  let embedded = embeddedFontFaceCache.get(fontFace.cssText);
  if (!embedded) {
    embedded = embedFontFace(fontFace).catch((error) => {
      // Do not cache failures, a later export may succeed. A missing font only degrades the text.
      embeddedFontFaceCache.delete(fontFace.cssText);
      logger.warn(`Could not embed font "${fontFace.family}" into the export`, error);
      return "";
    });
    embeddedFontFaceCache.set(fontFace.cssText, embedded);
  }
  return embedded;
}

/**
 * Drop-in replacement for html-to-image's `fontEmbedCSS` option, limited to the fonts the
 * captured element needs. Embedded font data is cached across captures.
 */
export async function getFontEmbedCssForElement(element: HTMLElement): Promise<string> {
  const fontFaces = selectFontFacesForExport(getDocumentFontFaces(), collectUsedFontFamilies(element));
  const embeddedFontFaces = await Promise.all(fontFaces.map(getEmbeddedFontFace));
  return embeddedFontFaces.filter(Boolean).join("\n");
}

export function clearFontEmbedCache() {
  embeddedFontFaceCache.clear();
}
